import { useCallback, useContext, useEffect, useState } from "react";

import {
  Alert,
  Button,
  Classes,
  Icon,
  Intent,
  Portal,
} from "@blueprintjs/core";
import { Calendar } from "@natscale/react-calendar";
import { isBefore, isEqual, startOfDay } from "date-fns";
import { toast } from "sonner";

import { Tooltip } from "@/components/helpers/ui/blueprint-overrides/Tooltip";
import { Box, Flex } from "@/components/layout/flexbox";
import { PageSizeContext } from "@/components/layout/PageSize";
import { BackDrop } from "@/components/pieces/BackDrop";
import { WorkingTimeslot } from "@/components/pieces/coaching/CoachingSettings/Timeslots/WorkingTimeslot";
import { Clickable } from "@/components/pieces/interaction/Clickable";
import { StyledButton } from "@/components/pieces/link/LinkButton";
import { ScrollableModal } from "@/components/pieces/ScrollableModal";
import { LIGHTEST_BACKGROUND, PORTAL_Z_INDEX } from "@/css/constants";
import {
  ScheduleOverridesDocument,
  ScheduleOverridesQuery,
  useDeleteScheduleOverrideMutation,
  useSaveScheduleOverridesMutation,
  useScheduleOverridesQuery,
} from "@/graphql";
import { NonNullableArrayItem } from "@/types/utils";
import { ensureNonNullArray } from "@/utils/array";
import { formatDate } from "@/utils/datetime";

import { SLOT_OPTIONS } from "./timeslots";

const CALENDAR_WIDTH = 450;
const MODAL_PADDING = 24;
const MODAL_SIZE = CALENDAR_WIDTH + MODAL_PADDING * 2;

type Override = NonNullableArrayItem<
  ScheduleOverridesQuery["scheduleOverrides"]
>;
interface OverrideModalProps {
  dates: Date[];
  setDates: (dates: Date[]) => void;
  isOpen: boolean;
  onClose: () => void;
  loading: boolean;
  handleSave: (dates: Date[], timeslots: string[]) => void;
  overrides: Override[];
}

function OverrideModal({
  dates,
  setDates,
  isOpen,
  onClose,
  loading,
  handleSave,
  overrides,
}: OverrideModalProps) {
  const [timeslots, setTimeslots] = useState<string[]>([]);
  const { windowWidth = 0 } = useContext(PageSizeContext);

  const onChange = useCallback(
    (value: Date[]) => {
      setDates(value);
    },
    [setDates],
  );

  useEffect(() => {
    if (!dates?.length) {
      setTimeslots([]);
      return;
    }

    if (dates.length === 1 && overrides?.length) {
      const matchingDate = overrides.find((o) =>
        isEqual(new Date(o.overrideDate), dates[0]),
      );
      setTimeslots(ensureNonNullArray(matchingDate?.timeslots));
      return;
    }
  }, [dates, overrides]);

  return (
    <Portal>
      <BackDrop visible={isOpen} zIndex={PORTAL_Z_INDEX} opacity={0.6} />
      <ScrollableModal
        visible={isOpen}
        onClose={onClose}
        verticallyCenter={true}
        canOutsideClickClose={false}
      >
        <Box
          backgroundColor="#FFF"
          className="round-corners"
          maxWidth={MODAL_SIZE}
        >
          <Flex
            px={MODAL_PADDING}
            py={3}
            alignItems="center"
            justifyContent="space-between"
            className="bbs"
          >
            <Box fontWeight={600} fontSize={22} pr={5}>
              Setup Custom Schedule
            </Box>
            <Tooltip content="Close">
              <Clickable
                onClick={(e) => {
                  e.stopPropagation();
                  onClose();
                }}
              >
                <Flex
                  width={32}
                  height={32}
                  alignItems="center"
                  justifyContent="center"
                >
                  <Icon icon="cross" size={24} />
                </Flex>
              </Clickable>
            </Tooltip>
          </Flex>
          <Box p={MODAL_PADDING}>
            <Box fontWeight={600} mb={3}>
              Select the date(s) you want to assign specific hours
            </Box>
            <Calendar
              className={IS_EXEC ? "exec-calendar" : "placement-calendar"}
              value={dates}
              startOfWeek={0}
              onChange={(value) => {
                if (Array.isArray(value)) {
                  onChange(value);
                }
              }}
              size={
                windowWidth > MODAL_SIZE
                  ? CALENDAR_WIDTH
                  : windowWidth - MODAL_PADDING * 2
              }
              fontSize={18}
              weekends={[]}
              isMultiSelector={true}
              isDisabled={(date) => isBefore(date, startOfDay(new Date()))}
            />
            {dates && !!dates.length && (
              <Box mt={24}>
                <WorkingTimeslot
                  date="When are you available?"
                  loading={loading}
                  slotOptions={SLOT_OPTIONS}
                  timeslots={timeslots}
                  onChange={setTimeslots}
                />
              </Box>
            )}
          </Box>
          <Box
            px={MODAL_PADDING}
            py={3}
            className="bts rbr rbl"
            backgroundColor={LIGHTEST_BACKGROUND}
          >
            <Button
              intent="primary"
              large={true}
              loading={loading}
              disabled={loading}
              onClick={() => {
                handleSave(dates, timeslots);
              }}
            >
              Save Schedule
            </Button>
          </Box>
        </Box>
      </ScrollableModal>
    </Portal>
  );
}

interface DateOverrideRowProps {
  override: Override;
  editDate: (date: string) => void;
  deleteDate: (date: string) => void;
}

function DateOverrideRow({
  override,
  editDate,
  deleteDate,
}: DateOverrideRowProps) {
  const [showConfirming, setShowConfirming] = useState(false);
  const formattedDate = formatDate(override.overrideDate, "MMM d, yyyy");

  const timeslots = ensureNonNullArray(override.timeslots);
  return (
    <>
      <Box py={3} className="bbs">
        <Box fontWeight={600}>{formattedDate}</Box>
        <Box mt={2}>
          <ul className="list-disc pl-6" style={{ paddingLeft: 16 }}>
            {timeslots.map((ts) => (
              <li key={ts}>
                {ts.split("-")[0]} - {ts.split("-")[1]}
              </li>
            ))}
            {!timeslots.length && <li>Not available</li>}
          </ul>
        </Box>
        <Box mt={2}>
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              editDate(override.overrideDate);
            }}
          >
            Edit
          </a>
          <span> · </span>
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              setShowConfirming(true);
            }}
          >
            Delete
          </a>
        </Box>
      </Box>
      <Alert
        cancelButtonText="Nevermind"
        confirmButtonText="Delete Override"
        icon="trash"
        intent={Intent.DANGER}
        isOpen={showConfirming}
        onCancel={() => {
          setShowConfirming(false);
        }}
        onConfirm={() => {
          deleteDate(override.overrideDate);
        }}
        canEscapeKeyCancel={true}
        canOutsideClickCancel={true}
      >
        <p>
          Are you sure you want to delete your schedule overrides for{" "}
          {formattedDate}?
        </p>
      </Alert>
    </>
  );
}

export function DateOverridesEditor() {
  const [showingModal, setShowingModal] = useState(false);
  const [dates, setDates] = useState<Date[]>([]);

  const { data, loading: fetching } = useScheduleOverridesQuery({
    onError: () => {},
  });

  const [save, { loading: saving }] = useSaveScheduleOverridesMutation({
    onCompleted: (d) => {
      if (d && d.saveScheduleOverrides && d.saveScheduleOverrides.ok) {
        toast.success("Saved");
        handleClose();
      }
    },
    onError: () => {
      toast.error("An error occurred. Please try again.");
    },
    refetchQueries: [{ query: ScheduleOverridesDocument }],
    awaitRefetchQueries: true,
  });

  const [deleteOverride, { loading: deleting }] =
    useDeleteScheduleOverrideMutation({
      onCompleted: (d) => {
        if (d && d.deleteScheduleOverride && d.deleteScheduleOverride.ok) {
          toast.success("Saved");
        }
      },
      onError: () => {
        toast.error("An error occurred. Please try again.");
      },
      refetchQueries: [{ query: ScheduleOverridesDocument }],
      awaitRefetchQueries: true,
    });

  const overrides = ensureNonNullArray(data?.scheduleOverrides);
  const loading = fetching || saving || deleting;

  const handleShow = () => {
    setDates([]);
    setShowingModal(true);
  };

  const handleClose = () => {
    setDates([]);
    setShowingModal(false);
  };

  const handleSave = (dates: Date[], timeslots: string[]) => {
    save({
      variables: {
        dates: dates.map((d) => formatDate(d, "yyyy-MM-dd")),
        timeslots,
      },
    });
  };

  const handleDelete = (date: string) => {
    deleteOverride({
      variables: {
        date,
      },
    });
  };

  return (
    <>
      <StyledButton onClick={handleShow}>Add date override</StyledButton>
      <Box className="bts" mt={24}>
        {loading && <Box height={30} className={Classes.SKELETON} />}
        {!loading &&
          overrides.map((override, idx) => (
            <DateOverrideRow
              key={idx}
              override={override}
              editDate={(date) => {
                setShowingModal(true);
                setDates([new Date(date)]);
              }}
              deleteDate={handleDelete}
            />
          ))}
        {!loading && (!overrides || !overrides.length) && (
          <Box py={3} className="bbs">
            No overrides set
          </Box>
        )}
      </Box>
      <OverrideModal
        isOpen={showingModal}
        dates={dates}
        setDates={setDates}
        onClose={handleClose}
        loading={loading}
        handleSave={handleSave}
        overrides={overrides}
      />
    </>
  );
}
